import React, { useState } from 'react';
import classnames from 'classnames';
import { Checkbox, Chip, SelectItem, TmdxSelect } from '@ds';
import { SelectItemType } from '@/types';
import { OrganType } from '@/api/apis/case';

export type CsOrganCertificationFieldProps = {
  value: OrganType[];
  onChange: (value: OrganType[]) => void;
  invalid: boolean;
  errorMessage?: string;
  title: string;
  items: SelectItemType[];
  isDisabled?: boolean;
};

export const CsOrganCertificationField = ({
  value,
  onChange,
  invalid,
  errorMessage,
  title,
  items,
  isDisabled,
}: CsOrganCertificationFieldProps) => {
  // useState is needed to track the open/closed state independently of value,
  // so the user can check the box first (while value is still empty) and then pick organs.
  const [isChecked, setIsChecked] = useState(() => value.length > 0);

  const handleCheckboxChange = (checked: boolean) => {
    setIsChecked(checked);
    if (!checked) {
      onChange([]);
    }
  };

  const handleSelectionChange = (keys: 'all' | Set<React.Key>) => {
    if (keys === 'all') return;
    onChange(Array.from(keys) as OrganType[]);
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1.5 w-28 shrink-0">
        <span className="text-hs font-secondary text-base-30">{title}</span>
        <Checkbox
          isSelected={isChecked}
          onValueChange={handleCheckboxChange}
          isDisabled={isDisabled}
          classNames={{ base: 'p-0 m-0 min-w-fit' }}
        />
      </div>
      <div className={classnames('w-full max-w-160 invisible', isChecked && 'visible')}>
        <TmdxSelect
          items={items}
          variant="bordered"
          isMultiline
          selectionMode="multiple"
          placeholder="Select organs"
          labelPlacement="outside"
          aria-label={title}
          isInvalid={invalid}
          isDisabled={isDisabled}
          errorMessage={errorMessage}
          selectedKeys={value ?? []}
          onSelectionChange={handleSelectionChange}
          renderValue={(selectedItems) => {
            const optionList = selectedItems.map((item) => item.data) as SelectItemType[];
            return (
              <div className="flex flex-wrap gap-2">
                {optionList.map((item) => (
                  <Chip key={item.value}>{item.name}</Chip>
                ))}
              </div>
            );
          }}
        >
          {(item) => {
            const selectItem = item as SelectItemType;
            return (
              <SelectItem textValue={selectItem.name} key={selectItem.value}>
                <div className="flex gap-2 items-center">
                  <span className="text-small">{selectItem.name}</span>
                </div>
              </SelectItem>
            );
          }}
        </TmdxSelect>
      </div>
    </div>
  );
};
